import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CalendarCheck, CheckCircle2, Clock, Loader2, MonitorPlay, Send, Users } from 'lucide-react';
import API from '../../utils/constant';
import { SITE, mailto } from './site';

const ROLES = [
  ['director', 'Director / principal'],
  ['vice_director', 'Vice director'],
  ['teacher', 'Teacher'],
  ['counsellor', 'Counsellor'],
  ['owner', 'Owner / board'],
  ['it', 'IT / administration'],
  ['other', 'Other'],
] as const;

const SCHOOL_TYPES = [
  ['private', 'Private'],
  ['public', 'Government'],
  ['university', 'University / college'],
] as const;

const TIMES = [
  ['any', 'Any time'],
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
] as const;

type Form = {
  school_name: string; contact_name: string; role: string; phone: string; email: string;
  city: string; school_type: string; student_count: string; preferred_date: string;
  preferred_time: string; message: string; website: string;
};

const EMPTY: Form = {
  school_name: '', contact_name: '', role: 'director', phone: '', email: '', city: '',
  school_type: '', student_count: '', preferred_date: '', preferred_time: 'any', message: '', website: '',
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const input =
  'w-full rounded-[12px] border border-line bg-ground px-3.5 py-2.5 text-[15px] text-ink outline-none transition ' +
  'placeholder:text-muted/70 focus:border-accent focus:ring-2 focus:ring-accent/25';

function Field({ label, optional, children, className = '' }:
  { label: string; optional?: boolean; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-[13px] font-semibold text-ink">
        {label}{optional && <span className="font-normal text-muted"> (optional)</span>}
      </span>
      {children}
    </label>
  );
}

function Pills({ value, options, onChange, label }:
  { value: string; options: readonly (readonly [string, string])[]; onChange: (v: string) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v}
          onClick={() => onChange(value === v && label === 'School type' ? '' : v)}
          className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${value === v
            ? 'border-navy bg-navy text-white dark:border-slate dark:bg-slate'
            : 'border-line bg-ground text-muted hover:text-ink'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function errorText(err: any): string {
  const status = err?.response?.status;
  if (status === 429) return 'You have sent several requests already. Please try again later or email us.';
  const data = err?.response?.data;
  if (status === 400 && data && typeof data === 'object') {
    const first = Object.values(data).flat()[0];
    if (typeof first === 'string') return first;
  }
  if (!err?.response) return 'Network error. Please check your connection and try again.';
  return 'Something went wrong. Please try again or email us.';
}

export default function BookDemo() {
  const [form, setForm] = useState<Form>(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  // The thank-you card is shorter than the form; keep it in view.
  useEffect(() => {
    if (done) card.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [done]);
  const set = (k: keyof Form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k: keyof Form) => (e: { target: { value: string } }) => set(k)(e.target.value);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await API.post('/api/public/demo-request/', {
        ...form,
        student_count: form.student_count ? Number(form.student_count) : null,
        preferred_date: form.preferred_date || null,
      });
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="demo" className="scroll-mt-20">
      <div className="mx-auto grid max-w-[1200px] gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div>
          <div className="font-mono text-[11.5px] font-semibold uppercase tracking-[0.1em] text-accent-ink">Book a demo</div>
          <h2 className="mt-3 font-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[42px]">
            See YADA with your own school in mind.
          </h2>
          <p className="mt-4 max-w-[480px] text-[16.5px] leading-relaxed text-muted">
            Tell us a little about your school and we will call you to set up a demo, at your school
            in Addis Ababa or online.
          </p>
          <ul className="mt-8 grid gap-4">
            {[
              { i: <MonitorPlay size={18} />, t: 'A live walk-through', d: 'The School panel, the staff app and the student app, with real screens.' },
              { i: <Clock size={18} />, t: 'About 30 minutes', d: 'Short enough for a director’s day, with time for questions.' },
              { i: <Users size={18} />, t: 'Bring your team', d: 'Teachers, counsellors and IT staff are welcome.' },
              { i: <CalendarCheck size={18} />, t: 'We call within one working day', d: 'To agree a time that suits you.' },
            ].map((x) => (
              <li key={x.t} className="flex list-none gap-3">
                <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-accent-bg text-accent-ink">{x.i}</span>
                <span>
                  <span className="block font-display font-bold text-ink">{x.t}</span>
                  <span className="block text-[14.5px] text-muted">{x.d}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div ref={card} className="rounded-[24px] border border-line bg-surface p-5 shadow-card sm:p-8">
          {done ? (
            <div role="status" className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-success-bg text-success">
                <CheckCircle2 size={34} />
              </div>
              <h3 className="mt-5 font-display text-[24px] font-extrabold tracking-[-0.02em] text-ink">Thank you, we got it.</h3>
              <p className="mt-2 max-w-[380px] text-muted">
                We will call {form.contact_name.split(/\s+/)[0] || 'you'} on <span className="font-semibold text-ink">{form.phone}</span> within
                one working day to arrange the demo for {form.school_name}.
              </p>
              <button type="button" onClick={() => { setForm(EMPTY); setDone(false); }}
                className="mt-6 text-sm font-semibold text-primary">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
              {/* Honeypot: hidden from people, filled by bots. */}
              <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={onInput('website')} /></label>
              </div>

              <Field label="School name" className="sm:col-span-2">
                <input className={input} required maxLength={200} value={form.school_name}
                  onChange={onInput('school_name')} placeholder="e.g. Bole Preparatory School" autoComplete="organization" />
              </Field>
              <Field label="Your name">
                <input className={input} required maxLength={120} value={form.contact_name}
                  onChange={onInput('contact_name')} autoComplete="name" />
              </Field>
              <Field label="Your role">
                <select className={input} value={form.role} onChange={onInput('role')}>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Phone">
                <input className={input} required type="tel" inputMode="tel" maxLength={30} value={form.phone}
                  onChange={onInput('phone')} placeholder="09… or +251…" autoComplete="tel" />
              </Field>
              <Field label="Email" optional>
                <input className={input} type="email" maxLength={254} value={form.email}
                  onChange={onInput('email')} autoComplete="email" />
              </Field>
              <Field label="City" optional>
                <input className={input} maxLength={120} value={form.city} onChange={onInput('city')}
                  placeholder="Addis Ababa" autoComplete="address-level2" />
              </Field>
              <Field label="Number of students" optional>
                <input className={input} type="number" min={1} max={100000} inputMode="numeric"
                  value={form.student_count} onChange={onInput('student_count')} />
              </Field>
              <div className="sm:col-span-2">
                <span className="mb-1.5 block text-[13px] font-semibold text-ink">
                  School type <span className="font-normal text-muted">(optional)</span>
                </span>
                <Pills label="School type" value={form.school_type} options={SCHOOL_TYPES} onChange={set('school_type')} />
              </div>
              <Field label="Preferred day" optional>
                <input className={input} type="date" min={todayIso()} value={form.preferred_date}
                  onChange={onInput('preferred_date')} />
              </Field>
              <div>
                <span className="mb-1.5 block text-[13px] font-semibold text-ink">Preferred time</span>
                <Pills label="Preferred time" value={form.preferred_time} options={TIMES} onChange={set('preferred_time')} />
              </div>
              <Field label="Anything we should know?" optional className="sm:col-span-2">
                <textarea className={`${input} min-h-[96px] resize-y`} maxLength={2000} value={form.message}
                  onChange={onInput('message')} placeholder="Grades, what you would like to see, questions…" />
              </Field>

              {error && (
                <p role="alert" className="rounded-[12px] bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink sm:col-span-2">{error}</p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
                <p className="text-[13px] text-muted">
                  Prefer email? <a href={mailto('YADA demo for our school')} className="font-semibold text-primary">{SITE.email}</a>
                </p>
                <button type="submit" disabled={sending}
                  className="inline-flex items-center gap-2 rounded-[14px] bg-accent px-6 py-3.5 font-semibold text-white transition hover:brightness-105 disabled:opacity-60">
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {sending ? 'Sending…' : 'Book a demo'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
